// Lazy load pages for better performance
import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";

import App from "../App";
import AuthLayout from "./layouts/AuthLayout";
import RouteAccessGuard from "./RouteAccessGuard";

// Loading component
const PageLoader = () => (
  <div className="flex h-screen w-full items-center justify-center">
    <div className="sk-circle">
      <div className="sk-child sk-circle1"></div>
      <div className="sk-child sk-circle2"></div>
      <div className="sk-child sk-circle3"></div>
      <div className="sk-child sk-circle4"></div>
      <div className="sk-child sk-circle5"></div>
      <div className="sk-child sk-circle6"></div>
      <div className="sk-child sk-circle7"></div>
      <div className="sk-child sk-circle8"></div>
      <div className="sk-child sk-circle9"></div>
      <div className="sk-child sk-circle10"></div>
      <div className="sk-child sk-circle11"></div>
      <div className="sk-child sk-circle12"></div>
    </div>
  </div>
);

// Wrapper for lazy loaded components
const withSuspense = (Component: React.LazyExoticComponent<React.ComponentType<unknown>>) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
);

// Public pages
const LoginPage = lazy(() => import("@admin/pages/login/LoginPage"));
const PartnerLoginPage = lazy(() => import("@admin/pages/partner-login/PartnerLoginPage"));

// Dashboard
const DashboardPage = lazy(() => import("@admin/pages/dashboard/DashboardPage"));
const DevDashboardPage = lazy(() => import("@admin/pages/dev-dashboard/DevDashboardPage"));

// Statistics
const StatisticsPage = lazy(() => import("@admin/pages/statistics/StatisticsPage"));

// Coupang Partners
const CoupangPartnersRevenuePage = lazy(
  () => import("@admin/pages/coupangPartners/CoupangPartnersRevenuePage"),
);

// Segment Analysis
const MarketingDashboardPage = lazy(() => import("@admin/pages/marketing/MarketingDashboardPage"));
const AcquisitionReportPage = lazy(() => import("@admin/pages/acquisition/AcquisitionReportPage"));
const AcquisitionReportDetailPage = lazy(() => import("@admin/pages/acquisition/AcquisitionReportDetailPage"));
const AcquisitionCohortPage = lazy(() => import("@admin/pages/acquisition/AcquisitionCohortPage"));
const AcquisitionDailyListPage = lazy(() => import("@admin/pages/acquisition/AcquisitionDailyListPage"));
const SegmentAnalysisPage = lazy(
  () => import("@admin/pages/segmentAnalysis/SegmentAnalysisPage"),
);

// Grant Requests
const GrantRequestsPage = lazy(
  () => import("@admin/pages/grantRequests/GrantRequestsPage"),
);

// Users
const UsersPage = lazy(() => import("@admin/pages/users/UsersPage"));
const UserDetailPage = lazy(() => import("@admin/pages/users/UserDetailPage"));
const UserEditPage = lazy(() => import("@admin/pages/users/UserEditPage"));

// Quiz Packs
const QuizPackListPage = lazy(() => import("@admin/pages/quiz-packs/list/QuizPackListPage"));
const TournamentDetailPage = lazy(() => import("@admin/pages/tournaments/TournamentDetailPage"));
const BattlesPage = lazy(() => import("@admin/pages/battles/BattlesPage"));
const BattleDetailPage = lazy(() => import("@admin/pages/battles/BattleDetailPage"));
const VocabWordsPage = lazy(() => import("@admin/pages/vocab-words/VocabWordsPage"));
const VocabWordEditPage = lazy(() => import("@admin/pages/vocab-words/VocabWordEditPage"));
const VocabJaReviewPage = lazy(() => import("@admin/pages/vocab-ja-review/VocabJaReviewPage"));
const VocabJaReviewSharePage = lazy(() => import("@admin/pages/vocab-ja-review/VocabJaReviewSharePage"));
const BankPage = lazy(() => import("@admin/pages/bank/BankPage"));
const BankTemplatePage = lazy(() => import("@admin/pages/bank/BankTemplatePage"));
const QuizPackCreatePage = lazy(() => import("@admin/pages/quiz-packs/create/QuizPackCreatePage"));
const QuizPackDetailPage = lazy(() => import("@admin/pages/quiz-packs/detail/QuizPackDetailPage"));
const QuizPackUpdatePage = lazy(() => import("@admin/pages/quiz-packs/update/QuizPackUpdatePage"));
const QuizCreatePage = lazy(() => import("@admin/pages/quiz-packs/quiz/create/QuizCreatePage"));
const QuizDetailPage = lazy(() => import("@admin/pages/quiz-packs/quiz/detail/QuizDetailPage"));
const RecommendPage = lazy(() => import("@admin/pages/quiz-packs/recommend/RecommendPage"));
const ReportListPage = lazy(() => import("@admin/pages/quiz-packs/report/ReportListPage"));
const ReportDetailPage = lazy(() => import("@admin/pages/quiz-packs/report/ReportDetailPage"));

// Curriculums
const CurriculumsPage = lazy(() => import("@admin/pages/curriculums/CurriculumsPage"));
const CurriculumCreatePage = lazy(() => import("@admin/pages/curriculums/CurriculumCreatePage"));
const CurriculumDetailPage = lazy(() => import("@admin/pages/curriculums/CurriculumDetailPage"));
const CurriculumEditPage = lazy(() => import("@admin/pages/curriculums/CurriculumEditPage"));

// Ad System
const AdDashboardPage = lazy(() => import("@admin/pages/ad-dashboard/AdDashboardPage"));
const AdAdvertisersPage = lazy(() => import("@admin/pages/ad-advertisers/AdAdvertisersPage"));
const AdAdvertiserCreatePage = lazy(() => import("@admin/pages/ad-advertisers/AdAdvertiserCreatePage"));
const AdAdvertiserDetailPage = lazy(() => import("@admin/pages/ad-advertisers/AdAdvertiserDetailPage"));
const AdAdvertiserEditPage = lazy(() => import("@admin/pages/ad-advertisers/AdAdvertiserEditPage"));
const AdCampaignsPage = lazy(() => import("@admin/pages/ad-campaigns/AdCampaignsPage"));
const AdCampaignCreatePage = lazy(() => import("@admin/pages/ad-campaigns/AdCampaignCreatePage"));
const AdCampaignDetailPage = lazy(() => import("@admin/pages/ad-campaigns/AdCampaignDetailPage"));
const AdCampaignEditPage = lazy(() => import("@admin/pages/ad-campaigns/AdCampaignEditPage"));
const RewardEventsPage = lazy(() => import("@admin/pages/reward-events/RewardEventsPage"));
const RewardEventCreatePage = lazy(() => import("@admin/pages/reward-events/RewardEventCreatePage"));
const RewardEventEditPage = lazy(() => import("@admin/pages/reward-events/RewardEventEditPage"));
const AdGroupsPage = lazy(() => import("@admin/pages/ad-groups/AdGroupsPage"));
const AdGroupCreatePage = lazy(() => import("@admin/pages/ad-groups/AdGroupCreatePage"));
const AdGroupDetailPage = lazy(() => import("@admin/pages/ad-groups/AdGroupDetailPage"));
const AdGroupEditPage = lazy(() => import("@admin/pages/ad-groups/AdGroupEditPage"));
const AdAdsPage = lazy(() => import("@admin/pages/ad-ads/AdAdsPage"));
const AdCreatePage = lazy(() => import("@admin/pages/ad-ads/AdCreatePage"));
const AdDetailPage = lazy(() => import("@admin/pages/ad-ads/AdDetailPage"));
const AdEditPage = lazy(() => import("@admin/pages/ad-ads/AdEditPage"));

// Notices
const NoticesPage = lazy(() => import("@admin/pages/notices/NoticesPage"));
const NoticeCreatePage = lazy(() => import("@admin/pages/notices/NoticeCreatePage"));
const NoticeDetailPage = lazy(() => import("@admin/pages/notices/NoticeDetailPage"));

// FAQs
const FaqsPage = lazy(() => import("@admin/pages/faqs/FaqsPage"));
const FaqCreatePage = lazy(() => import("@admin/pages/faqs/FaqCreatePage"));
const FaqDetailPage = lazy(() => import("@admin/pages/faqs/FaqDetailPage"));

// Promo Banners
const PromoBannersPage = lazy(() => import("@admin/pages/promo-banners/PromoBannersPage"));
const ServiceDocumentsPage = lazy(() => import("@admin/pages/service-documents/ServiceDocumentsPage"));
const ServiceDocumentDetailPage = lazy(() => import("@admin/pages/service-documents/ServiceDocumentDetailPage"));
const ServiceDocumentEditPage = lazy(() => import("@admin/pages/service-documents/ServiceDocumentEditPage"));
const PromoBannerCreatePage = lazy(() => import("@admin/pages/promo-banners/PromoBannerCreatePage"));
const PromoBannerDetailPage = lazy(() => import("@admin/pages/promo-banners/PromoBannerDetailPage"));

// Donation Organizations
const DonationOrganizationsPage = lazy(() => import("@admin/pages/donation-organizations/DonationOrganizationsPage"));
const DonationOrganizationCreatePage = lazy(() => import("@admin/pages/donation-organizations/DonationOrganizationCreatePage"));
const DonationOrganizationDetailPage = lazy(() => import("@admin/pages/donation-organizations/DonationOrganizationDetailPage"));

// Donation Campaigns
const DonationCampaignsPage = lazy(() => import("@admin/pages/donation-campaigns/DonationCampaignsPage"));
const DonationCampaignCreatePage = lazy(() => import("@admin/pages/donation-campaigns/DonationCampaignCreatePage"));
const DonationCampaignDetailPage = lazy(() => import("@admin/pages/donation-campaigns/DonationCampaignDetailPage"));

// Admins
const AdminsPage = lazy(() => import("@admin/pages/admins/AdminsPage"));
const AdminCreatePage = lazy(() => import("@admin/pages/admins/AdminCreatePage"));
const AdminDetailPage = lazy(() => import("@admin/pages/admins/AdminDetailPage"));
const AdminEditPage = lazy(() => import("@admin/pages/admins/AdminEditPage"));

// Global Settings
const GlobalSettingsPage = lazy(() => import("@admin/pages/global-settings/GlobalSettingsPage"));

// Partner API Keys
const PartnerApiKeysPage = lazy(() => import("@admin/pages/partner-api-keys/PartnerApiKeysPage"));
const PincruxExportPage = lazy(() => import("@admin/pages/partner-pincrux/PincruxExportPage"));

// 연속출석 테스트 지원 (QA용)
const StreakTestSupportPage = lazy(() => import("@admin/pages/test-support/StreakTestSupportPage"));

// Service Settings

// Versions
const VersionsPage = lazy(() => import("@admin/pages/versions/VersionsPage"));

// Short URL
const ShortUrlDashboardPage = lazy(() => import("@admin/pages/short-url/ShortUrlDashboardPage"));
const ShortUrlListPage = lazy(() => import("@admin/pages/short-url/ShortUrlListPage"));
const ShortUrlDetailPage = lazy(() => import("@admin/pages/short-url/ShortUrlDetailPage"));

// Study Groups
const StudyGroupsPage = lazy(() => import("@admin/pages/study-groups/StudyGroupsPage"));
const StudyGroupCreatePage = lazy(() => import("@admin/pages/study-groups/StudyGroupCreatePage"));
const StudyGroupDetailPage = lazy(() => import("@admin/pages/study-groups/StudyGroupDetailPage"));
const StudyGroupProfilePage = lazy(() => import("@admin/pages/study-groups/StudyGroupProfilePage"));
const StudyGroupJoinMethodPage = lazy(() => import("@admin/pages/study-groups/StudyGroupJoinMethodPage"));
const StudyGroupNicknamePage = lazy(() => import("@admin/pages/study-groups/StudyGroupNicknamePage"));
const StudyGroupMembersPage = lazy(() => import("@admin/pages/study-groups/StudyGroupMembersPage"));
const StudyGroupStatsSettingsPage = lazy(() => import("@admin/pages/study-groups/StudyGroupStatsSettingsPage"));

// Giftcon Orders
const GiftconOrdersPage = lazy(() => import("@admin/pages/giftcon-orders/GiftconOrdersPage"));

// Giftcon Brands
const GiftconBrandsPage = lazy(() => import("@admin/pages/giftcon-brands/GiftconBrandsPage"));

export const router = createBrowserRouter([
  {
    element: <App />,
    children: [
      // Redirect root to dashboard
      { path: "/", element: <Navigate to="/dashboard" replace /> },

      // Public routes
      { path: "/login", element: withSuspense(LoginPage) },
      { path: "/partner-login", element: withSuspense(PartnerLoginPage) },
      // 일본어 검수 공개 링크 — 로그인 없이 token 쿼리로만 (외부 검수자용)
      { path: "/share/vocab-ja-review", element: withSuspense(VocabJaReviewSharePage) },

      // Protected routes (with AuthLayout)
      {
        element: <AuthLayout />,
        children: [
          {
            // 경로별 권한/역할 검사 — 매핑은 src/libs/routeAccess.ts (사이드바와 공유)
            element: <RouteAccessGuard />,
            children: [
          // Dashboard
          { path: "/dashboard", element: withSuspense(DashboardPage) },
          { path: "/dev-dashboard", element: withSuspense(DevDashboardPage) },

          // Statistics
          { path: "/statistics", element: withSuspense(StatisticsPage) },

          // Coupang Partners
          {
            path: "/coupang-partners",
            element: withSuspense(CoupangPartnersRevenuePage),
          },

          // Marketing Dashboard
          { path: "/marketing-dashboard", element: withSuspense(MarketingDashboardPage) },
          { path: "/acquisition-report", element: withSuspense(AcquisitionReportPage) },
          { path: "/acquisition-report/detail", element: withSuspense(AcquisitionReportDetailPage) },
          { path: "/acquisition-report/cohort", element: withSuspense(AcquisitionCohortPage) },
          { path: "/acquisition-report/daily", element: withSuspense(AcquisitionDailyListPage) },

          // Segment Analysis
          { path: "/segment-analysis", element: withSuspense(SegmentAnalysisPage) },

          // Grant Requests
          { path: "/grant-requests", element: withSuspense(GrantRequestsPage) },

          // Users
          { path: "/users", element: withSuspense(UsersPage) },
          { path: "/users/:userId", element: withSuspense(UserDetailPage) },
          { path: "/users/:userId/edit", element: withSuspense(UserEditPage) },

          // Quiz Packs
          { path: "/vocab-words", element: withSuspense(VocabWordsPage) },
          { path: "/vocab-words/new", element: withSuspense(VocabWordEditPage) },
          { path: "/vocab-words/:wordId", element: withSuspense(VocabWordEditPage) },
          { path: "/vocab-ja-review", element: withSuspense(VocabJaReviewPage) },
          { path: "/bank", element: withSuspense(BankPage) },
          { path: "/bank/templates/:templateKey", element: withSuspense(BankTemplatePage) },
          { path: "/quiz-packs/list", element: withSuspense(QuizPackListPage) },
          { path: "/quiz-packs/create", element: withSuspense(QuizPackCreatePage) },
          { path: "/quiz-packs/:quizPackId", element: withSuspense(QuizPackDetailPage) },
          { path: "/quiz-packs/:quizPackId/update", element: withSuspense(QuizPackUpdatePage) },
          { path: "/quiz-packs/:quizPackId/quiz/create", element: withSuspense(QuizCreatePage) },
          { path: "/quiz-packs/:quizPackId/quiz/:quizId", element: withSuspense(QuizDetailPage) },
          { path: "/quiz-packs/recommend", element: withSuspense(RecommendPage) },
          { path: "/tournaments/:tournamentId", element: withSuspense(TournamentDetailPage) },
          { path: "/quiz-packs/report", element: withSuspense(ReportListPage) },
          { path: "/quiz-packs/report/:reportId", element: withSuspense(ReportDetailPage) },

          // Battles
          { path: "/battles", element: withSuspense(BattlesPage) },
          { path: "/battles/:battleId", element: withSuspense(BattleDetailPage) },

          // Curriculums
          { path: "/curriculums", element: withSuspense(CurriculumsPage) },
          { path: "/curriculums/create", element: withSuspense(CurriculumCreatePage) },
          { path: "/curriculums/:curriculumId", element: withSuspense(CurriculumDetailPage) },
          { path: "/curriculums/:curriculumId/edit", element: withSuspense(CurriculumEditPage) },

          // Ad System
          { path: "/ad-dashboard", element: withSuspense(AdDashboardPage) },
          { path: "/ad-advertisers", element: withSuspense(AdAdvertisersPage) },
          { path: "/ad-advertisers/create", element: withSuspense(AdAdvertiserCreatePage) },
          { path: "/ad-advertisers/:id", element: withSuspense(AdAdvertiserDetailPage) },
          { path: "/ad-advertisers/:id/edit", element: withSuspense(AdAdvertiserEditPage) },
          { path: "/ad-campaigns", element: withSuspense(AdCampaignsPage) },
          { path: "/ad-campaigns/create", element: withSuspense(AdCampaignCreatePage) },
          { path: "/ad-campaigns/:id", element: withSuspense(AdCampaignDetailPage) },
          { path: "/ad-campaigns/:id/edit", element: withSuspense(AdCampaignEditPage) },
          { path: "/reward-events", element: withSuspense(RewardEventsPage) },
          { path: "/reward-events/create", element: withSuspense(RewardEventCreatePage) },
          { path: "/reward-events/:id/edit", element: withSuspense(RewardEventEditPage) },
          { path: "/ad-groups", element: withSuspense(AdGroupsPage) },
          { path: "/ad-groups/create", element: withSuspense(AdGroupCreatePage) },
          { path: "/ad-groups/:id", element: withSuspense(AdGroupDetailPage) },
          { path: "/ad-groups/:id/edit", element: withSuspense(AdGroupEditPage) },
          { path: "/ad-ads", element: withSuspense(AdAdsPage) },
          { path: "/ad-ads/create", element: withSuspense(AdCreatePage) },
          { path: "/ad-ads/:id", element: withSuspense(AdDetailPage) },
          { path: "/ad-ads/:id/edit", element: withSuspense(AdEditPage) },

          // Notices
          { path: "/notices", element: withSuspense(NoticesPage) },
          { path: "/notices/create", element: withSuspense(NoticeCreatePage) },
          { path: "/notices/:noticeId", element: withSuspense(NoticeDetailPage) },

          // FAQs
          { path: "/faqs", element: withSuspense(FaqsPage) },
          { path: "/faqs/create", element: withSuspense(FaqCreatePage) },
          { path: "/faqs/:faqId", element: withSuspense(FaqDetailPage) },

          // Promo Banners
          { path: "/promo-banners", element: withSuspense(PromoBannersPage) },
          { path: "/service-documents", element: withSuspense(ServiceDocumentsPage) },
          { path: "/service-documents/:id", element: withSuspense(ServiceDocumentDetailPage) },
          { path: "/service-documents/:id/edit", element: withSuspense(ServiceDocumentEditPage) },
          { path: "/promo-banners/create", element: withSuspense(PromoBannerCreatePage) },
          { path: "/promo-banners/:bannerId", element: withSuspense(PromoBannerDetailPage) },

          // Donation Organizations
          { path: "/donation-organizations", element: withSuspense(DonationOrganizationsPage) },
          { path: "/donation-organizations/create", element: withSuspense(DonationOrganizationCreatePage) },
          { path: "/donation-organizations/:organizationId", element: withSuspense(DonationOrganizationDetailPage) },

          // Donation Campaigns
          { path: "/donation-campaigns", element: withSuspense(DonationCampaignsPage) },
          { path: "/donation-campaigns/create", element: withSuspense(DonationCampaignCreatePage) },
          { path: "/donation-campaigns/:campaignId", element: withSuspense(DonationCampaignDetailPage) },

          // Admins
          { path: "/admins", element: withSuspense(AdminsPage) },
          { path: "/admins/create", element: withSuspense(AdminCreatePage) },
          { path: "/admins/:adminId", element: withSuspense(AdminDetailPage) },
          { path: "/admins/:adminId/edit", element: withSuspense(AdminEditPage) },

          // Short URL
          { path: "/short-urls", element: withSuspense(ShortUrlDashboardPage) },
          { path: "/short-urls/list", element: withSuspense(ShortUrlListPage) },
          { path: "/short-urls/:shortCode", element: withSuspense(ShortUrlDetailPage) },

          // Study Groups
          { path: "/study-groups", element: withSuspense(StudyGroupsPage) },
          { path: "/study-groups/create", element: withSuspense(StudyGroupCreatePage) },
          { path: "/study-groups/:studyGroupId", element: withSuspense(StudyGroupDetailPage) },
          { path: "/study-groups/:studyGroupId/settings/profile", element: withSuspense(StudyGroupProfilePage) },
          { path: "/study-groups/:studyGroupId/settings/join-method", element: withSuspense(StudyGroupJoinMethodPage) },
          { path: "/study-groups/:studyGroupId/settings/nickname", element: withSuspense(StudyGroupNicknamePage) },
          { path: "/study-groups/:studyGroupId/settings/stats", element: withSuspense(StudyGroupStatsSettingsPage) },
          { path: "/study-groups/:studyGroupId/settings/members", element: withSuspense(StudyGroupMembersPage) },

          // Giftcon
          { path: "/giftcon/orders", element: withSuspense(GiftconOrdersPage) },
          { path: "/giftcon/brands", element: withSuspense(GiftconBrandsPage) },

          // Global Settings
          { path: "/global-settings", element: withSuspense(GlobalSettingsPage) },
          { path: "/partner-api-keys", element: withSuspense(PartnerApiKeysPage) },
          { path: "/partner-pincrux", element: withSuspense(PincruxExportPage) },
          { path: "/test-support/streak", element: withSuspense(StreakTestSupportPage) },

          // Service Settings

          // Versions
          { path: "/versions", element: withSuspense(VersionsPage) },
            ],
          },
        ],
      },
    ],
  },
]);
